import { Box, styled } from '@mui/material';
import { Suspense, lazy, useState } from 'react';
import { Outlet, useMatches } from 'react-router';

import { SIZES } from '../../assets/constants/constants';
import useIsMobile from '../../hooks/IsMobile';
import useIsMyScoopr from '../../hooks/IsMyScoopr';
import useBrainSession from '../../services/BrainSession/useBrainSession';
import { GlobalAlert } from '../GlobalAlert';
import Loader from '../Loader';

import Footer from './_components/Footer';
import MinScooprMenu from './_components/MinScooprMenu';
import SideBar from './_components/SideBar';
import TopBar from './_components/TopBar';

const ChatContainer = lazy(() => import('../Chat/_components/ChatContainer'));

const LayoutRoot = styled(Box)(({ theme }) => ({
  height: '100%',
  width: '100%',
  '--top-bar-height': `${SIZES.topBar.height.small}px`,
  [theme.breakpoints.up(SIZES.navigationChange)]: {
    '--top-bar-height': `${SIZES.topBar.height.large}px`,
  },
}));

const LayoutWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  flex: '1 1 auto',
  overflow: 'hidden',
  height: '100%',
  flexGrow: 1,
});

const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
});

const Content = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
});

const DashboardLayout = () => {
  const isMyScoopr = useIsMyScoopr();
  const { isMobile } = useIsMobile();
  const { showChatContainer } = useBrainSession();
  const [isSideBarOpen, setSideBarOpen] = useState(false);

  const matches = useMatches();
  const hideLayout = matches.some((match) => (match.handle as any)?.hideLayout);

  if (hideLayout) {
    return <Outlet />;
  }

  return (
    <Suspense fallback={<Loader />}>
      {showChatContainer ? (
        <LayoutRoot>
          <ChatContainer />
        </LayoutRoot>
      ) : (
        <LayoutRoot>
          <TopBar
            setSideBarOpen={() => setSideBarOpen(!isSideBarOpen)}
            isSideBarOpen={isSideBarOpen}
          />
          <SideBar
            onSideBarClose={() => setSideBarOpen(false)}
            isSideBarOpen={isSideBarOpen}
          />

          <LayoutWrapper>
            <ContentContainer role="main">
              <Content>
                <GlobalAlert />
                <Outlet />
              </Content>
            </ContentContainer>
          </LayoutWrapper>
          {isMyScoopr && isMobile ? <MinScooprMenu /> : <Footer />}
        </LayoutRoot>
      )}
    </Suspense>
  );
};

export default DashboardLayout;
